/**
 * M7 S10: the reactive gate clock, main-thread half (spec §10.1–§10.2, §8.3; plan §2 "The gate clock").
 *
 * `storyMult` is **derived** from the Career, never stored as its own truth, so it can't reset on load (T19). The
 * bridge sends it as-is with `setStoryClock` on load, on a fresh Reach and after every story change or online day;
 * the worker (S19) applies `min(storyExtractionMult × storyMult, storyMultCap)` to **AI extraction only** (the
 * player's own tonnes always count × 1). Every number lives in `clock.json`.
 *
 * - Before the finale: `max(tier row, idleRamp) + alertStep × alerts`. Tiers 0–2 (and an absent `storyTier`) share
 *   `baseMult`. The idleRamp counts online days while the tier is 0–2; it starts after `startOnlineDay`, adds
 *   `step` for each started block of `everyOnlineDays`, caps at `idleRamp.cap`, and freezes at the tier-3 crossing.
 *   Alerts are derived from flags and latched into `clock.alerts` by `syncClock` (Tethys doesn't forget).
 * - After the commit (`finale.paths` non-empty): the row of the newest path in force **replaces** the tier row, the
 *   idleRamp and the alerts. Expose and Guardians both in force take the lower row. A Blackmail exit adds the
 *   surcharge for `blackmailSurcharge.onlineDays` online days from `finale.exitOnlineDay`.
 * - Tier drops: one per crossing, once per tier per career (`clock.tierDropsFired`). A jump fires only the newest
 *   tier's drop and marks the skipped tiers, so drops never stack. The worker clamps with `tierDropAfter`.
 *
 * D-TBD: S10 clock derivation details: the idleRamp steps on each *started* `everyOnlineDays` block (day 61 → +step); the
 * idleRamp freezes at the tier-3 crossing (`clock.idleDays`, written when the first drop fires); a pending Expose vote
 * uses `finaleRows.exposePending` (2.0, "Tethys strips while it can"); a Guardians path with no recorded floor reads
 * the post-floor row; a tier jump fires one drop (the newest tier's); alerts latch; values are rounded to 1e-3.
 *
 * Pure and event-driven: nothing here runs per frame.
 */
import type { Career } from '../career/types'
import { storyData } from './data'
import type { ClockConfig } from './schemas'
import { storyOf, withStory } from './state'
import {
  CLOCK_ALERTS,
  type ClockAlert,
  type FinalePath,
  type FinaleRow,
  type StoryCommand,
  type StoryFinale,
  type StoryResult,
  type StoryState,
} from './types'

const MAX_TIER = 5
const FIRST_LADDER_TIER = 3

function round3(x: number): number {
  return Math.round(x * 1000) / 1000
}

function clockCfg(cfg: ClockConfig | undefined): ClockConfig {
  return cfg ?? storyData().clock
}

/** The claimed tier, 0..5 (an absent or out-of-range `storyTier` reads as the nearest valid tier). */
export function storyTierOf(career: Career): number {
  const t = career.storyTier ?? 0
  if (!Number.isFinite(t) || t <= 0) return 0
  return t >= MAX_TIER ? MAX_TIER : Math.floor(t)
}

function finaleStarted(story: StoryState): boolean {
  return story.finale.paths.length > 0
}

// ---------------------------------------------------------------------------------------------
// The ladder (before the finale)
// ---------------------------------------------------------------------------------------------

/** The tier row: `baseMult` for tiers 0–2, else `tierRows[tier − 3].mult`. */
export function tierRowOf(career: Career, cfg?: ClockConfig): number {
  const c = clockCfg(cfg)
  const tier = storyTierOf(career)
  if (tier < FIRST_LADDER_TIER) return c.baseMult
  const row = c.tierRows[tier - FIRST_LADDER_TIER]
  return row === undefined ? c.baseMult : row.mult
}

/**
 * Online days counted toward the idleRamp. While the ramp is live (tier 0–2, or tier 3+ before the first drop has
 * fired) it is every online day so far; once the tier-3 crossing has been synced it is the frozen `clock.idleDays`.
 */
export function idleDaysOf(career: Career): number {
  const story = storyOf(career)
  const live = storyTierOf(career) < FIRST_LADDER_TIER || story.clock.tierDropsFired.length === 0
  if (live && !finaleStarted(story)) return Math.max(story.clock.idleDays, story.onlineDays)
  return story.clock.idleDays
}

/** idleRamp steps taken after `idleDays` online days at tiers 0–2 (capped where the ramp reaches its cap). */
export function idleRampSteps(idleDays: number, cfg?: ClockConfig): number {
  const c = clockCfg(cfg)
  const r = c.idleRamp
  if (idleDays <= r.startOnlineDay) return 0
  const steps = Math.ceil((idleDays - r.startOnlineDay) / r.everyOnlineDays)
  const maxSteps = Math.max(0, Math.ceil(round3((r.cap - c.baseMult) / r.step)))
  return steps < maxSteps ? steps : maxSteps
}

/** The idleRamp's value for this career (`baseMult` until it starts). */
export function idleRampOf(career: Career, cfg?: ClockConfig): number {
  const c = clockCfg(cfg)
  const v = c.baseMult + c.idleRamp.step * idleRampSteps(idleDaysOf(career), c)
  return round3(v < c.idleRamp.cap ? v : Math.max(c.idleRamp.cap, c.baseMult))
}

/**
 * The alerts in force (§10.2): `manifest_sold`, the legal archive request (`archive_legal`), `okafor_public` with
 * Okafor aboard, plus any latched in `clock.alerts`. In `CLOCK_ALERTS` order.
 */
export function alertsOf(career: Career): ClockAlert[] {
  const story = storyOf(career)
  const on: Record<ClockAlert, boolean> = {
    manifestSold: story.flags.manifest_sold === true,
    legalArchive: story.flags.archive_legal === true,
    okaforPublicAboard: story.flags.okafor_public === true && story.okaforState === 'aboard',
  }
  for (const a of story.clock.alerts) on[a] = true
  return CLOCK_ALERTS.filter((a) => on[a])
}

// ---------------------------------------------------------------------------------------------
// Finale rows (spec §10.2 post-finale table, §8.3 ladder)
// ---------------------------------------------------------------------------------------------

/** Paths still in force (a left Blackmail is not, whether or not the caller kept it in `paths`). */
export function pathsInForce(finale: StoryFinale): FinalePath[] {
  const out: FinalePath[] = []
  for (const p of finale.paths) if (p !== finale.from && !out.includes(p)) out.push(p)
  return out
}

/** One path's own row at `onlineDays`. */
export function pathRow(path: FinalePath, finale: StoryFinale, onlineDays: number): FinaleRow {
  switch (path) {
    case 'expose':
      return finale.vote === 'passed' ? 'exposePassed' : finale.vote === 'failed' ? 'exposeFailed' : 'exposePending'
    case 'guardians':
      return finale.floorUntilOnlineDay !== undefined && onlineDays <= finale.floorUntilOnlineDay ? 'guardiansFloor' : 'guardians'
    case 'blackmail':
      return 'blackmail'
    case 'sell':
      return 'sell'
  }
}

/**
 * The post-finale row in force, or undefined before the commit: the newest path's row, except that Expose and
 * Guardians both in force take the lower of their two rows.
 */
export function finaleRowOf(career: Career, cfg?: ClockConfig): FinaleRow | undefined {
  const c = clockCfg(cfg)
  const story = storyOf(career)
  const paths = pathsInForce(story.finale)
  const newest = paths[paths.length - 1]
  if (newest === undefined) return undefined
  if (paths.includes('expose') && paths.includes('guardians')) {
    const e = pathRow('expose', story.finale, story.onlineDays)
    const g = pathRow('guardians', story.finale, story.onlineDays)
    return c.finaleRows[g] < c.finaleRows[e] ? g : e
  }
  return pathRow(newest, story.finale, story.onlineDays)
}

/** The Blackmail exit surcharge in force now (0 when none): `add` for `onlineDays` online days from the exit. */
export function blackmailSurchargeOf(career: Career, cfg?: ClockConfig): number {
  const c = clockCfg(cfg)
  const { finale, onlineDays } = storyOf(career)
  if (finale.from !== 'blackmail' || finale.exitOnlineDay === undefined) return 0
  const since = onlineDays - finale.exitOnlineDay
  return since >= 0 && since < c.blackmailSurcharge.onlineDays ? c.blackmailSurcharge.add : 0
}

// ---------------------------------------------------------------------------------------------
// storyMult
// ---------------------------------------------------------------------------------------------

/**
 * The story multiplier for this career (spec §10.2), uncapped: the worker caps the product at `storyMultCap`
 * (`gate/stability.ts storyMultiplier`). Rounded to 1e-3 so the sent value is stable across re-applies.
 */
export function storyMultOf(career: Career, cfg?: ClockConfig): number {
  const c = clockCfg(cfg)
  const row = finaleRowOf(career, c)
  if (row !== undefined) return round3(c.finaleRows[row] + blackmailSurchargeOf(career, c))
  const tier = tierRowOf(career, c)
  const idle = idleRampOf(career, c)
  return round3((tier > idle ? tier : idle) + c.alertStep * alertsOf(career).length)
}

/** The idempotent `setStoryClock` command for this career (the re-apply list, S6/S26). */
export function storyClockCommand(career: Career, cfg?: ClockConfig): StoryCommand {
  return { type: 'setStoryClock', storyMult: storyMultOf(career, cfg) }
}

// ---------------------------------------------------------------------------------------------
// Tier drops
// ---------------------------------------------------------------------------------------------

export interface TierDrop {
  tier: number
  drop: number
  cause: string
}

/** The tier drop still owed for the claimed tier, or null (none below tier 3; once per tier per career). */
export function pendingTierDrop(career: Career, cfg?: ClockConfig): TierDrop | null {
  const c = clockCfg(cfg)
  const tier = storyTierOf(career)
  if (tier < FIRST_LADDER_TIER) return null
  if (storyOf(career).clock.tierDropsFired.includes(tier)) return null
  const row = c.tierRows[tier - FIRST_LADDER_TIER]
  return row === undefined ? null : { tier, drop: row.drop, cause: row.cause }
}

/**
 * Stability after a tier drop (for the worker, S19): `stability − drop`, never below `dropFloor` and never raising a
 * value already under it. `dropFloor` sits above `closedAt`, so a drop never closes the gate.
 */
export function tierDropAfter(stability: number, drop: number, cfg?: ClockConfig): number {
  const floor = clockCfg(cfg).dropFloor
  if (stability <= floor) return stability
  const s = stability - drop
  return s > floor ? s : floor
}

// ---------------------------------------------------------------------------------------------
// Sync: the one entry point after a story change
// ---------------------------------------------------------------------------------------------

function sameNumbers(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i])
}

/**
 * Brings `career.story.clock` up to date and returns what to send: the pending tier drop (fired once; skipped
 * tiers are marked; the idleRamp freezes at the first one), the latched alerts, the cached finale row, and the
 * idempotent `setStoryClock`. Calling it twice gives the same career and only `setStoryClock`.
 */
export function syncClock(career: Career, cfg?: ClockConfig): StoryResult<Career> {
  const c = clockCfg(cfg)
  const drop = pendingTierDrop(career, c)
  const before = storyOf(career)
  const idleDays = idleDaysOf(career)
  const alerts = alertsOf(career)
  const finaleRow = finaleRowOf(career, c)
  let fired = before.clock.tierDropsFired
  if (drop !== null) {
    const next = new Set(fired)
    for (let t = FIRST_LADDER_TIER; t <= drop.tier; t++) next.add(t)
    fired = [...next].sort((a, b) => a - b)
  }
  const clock = before.clock
  const changed =
    !sameNumbers(fired, clock.tierDropsFired) ||
    idleDays !== clock.idleDays ||
    alerts.length !== clock.alerts.length ||
    alerts.some((a, i) => a !== clock.alerts[i]) ||
    finaleRow !== clock.finaleRow
  const next = changed
    ? withStory(career, (s) => {
        const nextClock = { ...s.clock, alerts, idleDays, tierDropsFired: fired }
        if (finaleRow === undefined) delete nextClock.finaleRow
        else nextClock.finaleRow = finaleRow
        return { ...s, clock: nextClock }
      })
    : career
  const commands: StoryCommand[] = []
  if (drop !== null) commands.push({ type: 'storyTierDrop', tier: drop.tier, drop: drop.drop, cause: drop.cause })
  commands.push(storyClockCommand(next, c))
  return { career: next, commands, effects: [] }
}

/**
 * The idleRamp's Wire item for the online day just counted (call it right after `countOnlineDay`): a `postWire`
 * when that day took the ramp a step, else nothing. Stateless: it compares the step at `idleDays` and the day before.
 */
export function idleRampWireOnDay(career: Career, cfg?: ClockConfig): StoryCommand[] {
  const c = clockCfg(cfg)
  const story = storyOf(career)
  if (finaleStarted(story) || storyTierOf(career) >= FIRST_LADDER_TIER) return []
  const d = idleDaysOf(career)
  if (d <= 0 || idleRampSteps(d, c) <= idleRampSteps(d - 1, c)) return []
  return [{ type: 'postWire', headline: c.idleRampWire.headline, severity: c.idleRampWire.severity, source: c.idleRampWire.source }]
}
